import { getSessionToken } from './getSessionToken';

/**
 * Gets authentication headers for API requests
 *
 * Architecture:
 *
 * ELECTRON (Current Setup):
 * - User authenticates in EXTERNAL browser (Safari/Chrome/Firefox)
 * - Tokens stored in SECURE STORAGE (main process only)
 * - NO cookies in Electron's BrowserWindow session
 * - This function returns {} (empty headers)
 * - Main process intercepts ALL HTTP requests via onBeforeSendHeaders
 * - Authorization header injected automatically from secure storage
 * - Token NEVER exposed to renderer process ✅ (security best practice)
 *
 * NON-ELECTRON (if deployed separately in future):
 * - User authenticates in-app (same browser session)
 * - Descope SDK reads token from cookies
 * - This function returns Authorization header
 *
 * See: apps/ui/terra-proxy/src/electron/main.ts -> session.webRequest.onBeforeSendHeaders
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  if (typeof window !== 'undefined' && window.electronAPI?.auth) {
    try {
      const tokenResult = await window.electronAPI.auth.getAccessToken();
      if (tokenResult?.success && tokenResult.accessToken) {
        return {
          Authorization: `Bearer ${tokenResult.accessToken}`,
        };
      }
      console.warn('[AuthHeaders] Electron access token missing', {
        success: tokenResult?.success,
      });
    } catch (error: unknown) {
      console.warn('[AuthHeaders] Electron access token lookup failed', {
        error: error instanceof Error ? error.message : String(error),
      });
    }

    return {};
  }

  const sessionToken = getSessionToken();

  if (sessionToken) {
    console.info('[AuthHeaders] Session token found', {
      length: sessionToken.length,
    });
    return {
      Authorization: `Bearer ${sessionToken}`,
    };
  }

  console.warn('[AuthHeaders] No token available');
  return {};
}
